import React, { useState } from 'react';
import { useTier, controlHeight } from '../lib/tiers';
import { stepValue, typedValue, STEPPER_MAX } from '../lib/qtyStepper';
import './QtyStepper.css';

// THE SHARED QUANTITY STEPPER - #746 and #760, Session 79.22b.6.3.1.
//
// − / a typed number / +. Gate 746 asks for it (F5, "A new shared quantity
// stepper, src/components/QtyStepper.js") because Stede had none: five screens
// each typed their own − and + at 30 to 38px, under the finger floor (#760),
// and the Welding page's size sheet needs the same control at 48. One control
// means one rule for what a press does and one size rule for every screen.
//
// THE SIZE COMES FROM THE POINTER, NOT THE WIDTH (#417 D6, controlHeight()):
// to a finger, `size="commit"` is 48 (the Welding sheet, F4: "− and + are 48")
// and `size="min"` is 40, the finger floor; to a mouse it is 32, the app's
// standard control height - Colby's ruling at 79.22b.6.3.1 (R1 D6).
//
// WHAT A PRESS DOES is src/lib/qtyStepper.js: a step of 1, + on an empty box
// gives 1, nothing below `min` or above `max`, never disabled; whole numbers
// only, and a decimal point is REFUSED with "Whole numbers only." rather than
// dropped, which would turn a typed 2.5 into 25 (R1 D5).
//
// The value is a string of digits, '' when empty; `onChange` receives the same.
// The look is QtyStepper.css - Grok's drawn stepper (gate 746, F2: `.step`,
// `.pm`, `.num`). The box's type is set inline because the app's global input
// rule outranks any one class (index.css, #166), and it is never below 16px
// (locked rule 12).
//
// ON THE INVENTORY SCREENS (#760, Session 79.22b.6.3.2, the approved before/after
// drawing): only the − and + change. Each screen keeps its own box - its width,
// height, type and colours, Item Detail's red when stock will go negative, the
// request's purple - through `boxStyle`, usually built by `fieldBox()` below.
// `children` is what sits beside the + in the same row (a unit, "→ 8
// remaining", a hint), so "Whole numbers only." still lands under the row
// rather than beside it; `rowStyle` is the row's own spacing.
export function fieldBox(width, height, fontSize, extra) {
  return {
    flex: 'none', width, height, borderRadius: 10, border: '1px solid #E0E0E0',
    fontFamily: 'var(--font-body)', fontSize, fontWeight: 700, letterSpacing: 'normal',
    color: '#0F0F0F', background: 'white',
    ...(extra || {}),
  };
}

// Item Detail's and the catalog's Use box when the quantity would take stock
// below zero - a warning, never a block (qty_on_hand can go negative).
export const NEGATIVE_BOX = Object.freeze({ color: '#A32D2D', background: '#FCEBEB', border: '1px solid #F09595' });
// The request's purple, on its one-item box and every cart row.
export const REQUEST_BOX = Object.freeze({ color: '#3C3489', background: '#EEEDFE', border: '1px solid #CECBF6' });

export default function QtyStepper({
  value,
  onChange,
  min = 0,
  max = STEPPER_MAX,
  size = 'min',
  id,
  label,
  fewerLabel = 'One fewer',
  moreLabel = 'One more',
  className = '',
  boxStyle,
  rowStyle,
  selectOnFocus = true,
  children,
}) {
  const { coarse } = useTier();
  const [refused, setRefused] = useState(null);
  const h = coarse ? controlHeight(true, size) : controlHeight(false, 'commit');

  const step = delta => {
    setRefused(null);
    onChange?.(stepValue(value, delta, { min, max }));
  };
  const typed = e => {
    const r = typedValue(value, e.target.value);
    setRefused(r.refused);
    if (r.value !== String(value ?? '')) onChange?.(r.value);
  };

  return (
    <>
      <div className={`qty-step${className ? ` ${className}` : ''}`} style={rowStyle}>
        <button type="button" className="qty-step-btn" aria-label={fewerLabel}
          style={{ width: h, height: h }} onClick={() => step(-1)}>−</button>
        <input
          id={id}
          className="qty-step-box"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          enterKeyHint="done"
          aria-label={id ? undefined : label}
          value={value ?? ''}
          onChange={typed}
          onFocus={selectOnFocus ? (e => e.target.select()) : undefined}
          style={{
            height: h,
            fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 26, letterSpacing: '.5px',
            ...(boxStyle || {}),
            // Locked rule 12 holds whatever a screen passes: under 16px an
            // iPhone zooms the page in when the box is tapped (#793).
            ...(boxStyle && boxStyle.fontSize < 16 ? { fontSize: 16 } : {}),
          }}
        />
        <button type="button" className="qty-step-btn" aria-label={moreLabel}
          style={{ width: h, height: h }} onClick={() => step(1)}>+</button>
        {children}
      </div>
      {refused && <p className="qty-step-note" role="alert">{refused}</p>}
    </>
  );
}
